// /methodology — how MedReview grades safety flags. Walter (retired
// internist persona) explicitly demanded this in round-2 feedback
// alongside the Advisory Board page (#24, #25). The page is the
// trust artifact for clinicians, researchers, and IRB-minded readers;
// it should read as a methods section, not marketing.

import { Link } from 'react-router-dom';
import { AppPage } from '../layouts/AppPage';

interface Source {
  name: string;
  version: string;
  year: number;
  citation: string;
  license: string;
}

const SOURCES: Source[] = [
  {
    name: 'Beers Criteria',
    version: '2023',
    year: 2023,
    citation:
      'American Geriatrics Society 2023 Updated AGS Beers Criteria for Potentially Inappropriate Medication Use in Older Adults. J Am Geriatr Soc. 2023;71(7):2052-2081. doi:10.1111/jgs.18372',
    license: 'AGS license, not redistributable as a list. References only.',
  },
  {
    name: 'STOPP/START',
    version: 'v3',
    year: 2023,
    citation:
      "O'Mahony D, Cherubini A, Guiteras AR, et al. STOPP/START criteria for potentially inappropriate prescribing in older people: version 3. Eur Geriatr Med. 2023;14(4):625-632. doi:10.1007/s41999-023-00777-y",
    license: 'CC BY 4.0',
  },
  {
    name: 'CRIDECO Anticholinergic Cognitive Burden Scale',
    version: '2022',
    year: 2022,
    citation:
      'Ramos H, Moreno L, Perez-Tur J, Chafer-Pericas C, Garcia-Lluch G, Pardo J. CRIDECO Anticholinergic Load Scale: An Updated Anticholinergic Burden Scale. J Pers Med. 2022;12(2):207. doi:10.3390/jpm12020207',
    license: 'CC BY 4.0',
  },
  {
    name: 'CMS Quality Payment Program Measure 238',
    version: '2024.1',
    year: 2024,
    citation:
      'Centers for Medicare & Medicaid Services. Quality Payment Program Measure 238: Use of High-Risk Medications in Older Adults. https://qpp.cms.gov/',
    license: 'Public domain',
  },
  {
    name: 'Ototoxicity catalogue',
    version: '2026.1',
    year: 2026,
    citation:
      'Cianfrone G et al. 2011 drug-induced ototoxicity guide; AAO-HNS 2024 ototoxicity monitoring statement.',
    license: 'Public-domain summary; consult primary citations',
  },
];

interface SeverityRubric {
  level: 'CRITICAL' | 'MAJOR' | 'MODERATE' | 'MINOR';
  meaning: string;
  examples: string;
}

const RUBRIC: SeverityRubric[] = [
  {
    level: 'CRITICAL',
    meaning:
      'A combination or dose that is contraindicated for this patient population per cited guideline. Likely to cause serious adverse event within days.',
    examples:
      'Warfarin + ibuprofen co-prescription. ACB total at or above 6. CMS HRM list overlap in renal impairment.',
  },
  {
    level: 'MAJOR',
    meaning:
      'A combination or class strongly cautioned against. Adverse event probable within weeks.',
    examples:
      'Triple anticoagulation. ACB total 4 to 5. STOPP B7 (aspirin without indication after 65).',
  },
  {
    level: 'MODERATE',
    meaning: 'A medication on a watch list. Conversation worth having at the next visit.',
    examples:
      'ACB total 3. CMS HRM list overlap without renal impairment. First-generation antihistamine after 65 (STOPP D9).',
  },
  {
    level: 'MINOR',
    meaning: 'Educational, often dose- or duration-dependent. No immediate action required.',
    examples:
      'Ototoxic class member at standard dose. Drug-food note like warfarin + leafy greens.',
  },
];

export function Methodology(): JSX.Element {
  return (
    <AppPage title="Methodology" back={{ to: '/account', label: 'Account' }}>
      <section className="account-section">
        <p style={{ fontSize: 16, margin: 0 }}>
          Our safety flags come from public clinical lists. Siggy turns each flag into plain words,
          but never makes one up. This page lists every source we use.
        </p>
        <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginTop: 8 }}>
          Last review by Dr. Marlee Grounds, PharmD on 2026-04-15. We check the lists every three
          months.{' '}
          <Link to="/advisory-board" className="account-link" style={{ fontWeight: 600 }}>
            See our Medical Advisory Board.
          </Link>
        </p>
      </section>

      <section className="account-section">
        <h3 className="account-section__title">Sources</h3>
        <div style={{ display: 'grid', gap: 14 }}>
          {SOURCES.map((s) => (
            <div
              key={s.name}
              data-source-card="true"
              style={{
                padding: 12,
                border: '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <div style={{ margin: 0, fontWeight: 700, fontSize: 16 }}>
                {s.name} ({s.version}, {s.year})
              </div>
              <div
                style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--color-text-secondary)' }}
              >
                {s.citation}
              </div>
              <div style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                License: {s.license}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="account-section">
        <h3 className="account-section__title">Severity rubric</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
          <thead>
            <tr style={{ borderBottom: '1.5px solid var(--color-border)' }}>
              <th style={{ textAlign: 'left', padding: 8, fontWeight: 600 }}>Level</th>
              <th style={{ textAlign: 'left', padding: 8, fontWeight: 600 }}>What it means</th>
              <th style={{ textAlign: 'left', padding: 8, fontWeight: 600 }}>Examples</th>
            </tr>
          </thead>
          <tbody>
            {RUBRIC.map((r) => (
              <tr
                key={r.level}
                style={{
                  borderBottom: '1px solid var(--color-border-subtle)',
                  verticalAlign: 'top',
                }}
              >
                <td style={{ padding: 8, fontWeight: 700 }}>{r.level}</td>
                <td style={{ padding: 8 }}>{r.meaning}</td>
                <td style={{ padding: 8, fontSize: 14 }}>{r.examples}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="account-section">
        <h3 className="account-section__title">What MedReview will not do</h3>
        <ul style={{ paddingLeft: 20, margin: 0, lineHeight: 1.6 }}>
          <li>Tell you what dose to take. Your doctor sets that.</li>
          <li>Diagnose you. We just flag concerns to talk about.</li>
          <li>Sell your list. Read our privacy page.</li>
          <li>Take the place of your doctor or pharmacist.</li>
        </ul>
      </section>
    </AppPage>
  );
}
